import { useEffect, useMemo } from 'react';
import i18n from 'i18next';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

import CaseType from '../../../../../shared/enums/CaseType';
import { getCurrentTranslation } from '../../../../../shared/utils/helpers';
import { trpc } from '@/config/trpc';
import { useGetEnums } from '../../../../../queries';

import FormikAutocomplete from '../../../../../shared/formik/FormikAutocomplete';
import FormikEnumSelector from '../../../../../shared/formik/FormikEnumSelector';
import FormikTeamUserSelector from '../../../../../shared/formik/FormikTeamUserSelector';
import FormikTextField from '../../../../../shared/formik/FormikTextField';
import FormikYesNo from '../../../../../shared/formik/FormikYesNo';
import Row from '../../../../../shared/components/2.0/layout/Row';

const CaseInfoStep = () => {
  const { t } = useTranslation('common');
  const { language } = i18n;
  const { values, setFieldValue } = useFormikContext();
  const { automaticReference } = values;
  const { reference } = values;

  const {
    enums: { priorities },
  } = useGetEnums();

  const { data: caseClassificationsData = [] } = trpc.team.case.classification.list.useQuery({
    caseTypes: [CaseType.InsuranceClaim],
  });

  const caseClassifications = useMemo(
    () =>
      caseClassificationsData.toSorted((a, b) => {
        const translationA = getCurrentTranslation(a.translations, language) ?? '';
        const translationB = getCurrentTranslation(b.translations, language) ?? '';
        return translationA.localeCompare(translationB);
      }),
    [caseClassificationsData, language],
  );

  useEffect(() => {
    setFieldValue('reference', '', true);
  }, [automaticReference, setFieldValue]);

  return (
    <>
      <div>
        <h3 style={{ fontWeight: 'bold' }}>{t('common:caseInfo')}</h3>
        <p style={{ marginBottom: '20px', marginTop: '0px' }}>
          {t('common:enterAReferenceForYourCase')}
        </p>
        <div>
          <FormikYesNo
            infoText={automaticReference ? t('common:automaticReferenceWillBeAssigned') : null}
            name="automaticReference"
            noText={t('common:manualReference')}
            yesText={t('common:automaticNumber')}
          />
        </div>
        {!automaticReference && (
          <div style={{ marginTop: '15px' }}>
            <FormikTextField
              autoFocus
              required
              defaultValue={reference}
              label={t('common:reference')}
              name="reference"
              variant="standard"
            />
          </div>
        )}
      </div>
      <div className="mt-3">
        <Row>
          <div className="flex-grow basis-1">
            <FormikTeamUserSelector required label={t('common:administrators')} name="admins" />
          </div>
          <div className="flex-grow basis-1">
            <FormikEnumSelector
              required
              enumValues={priorities}
              id="priority"
              label={t('common:priority')}
              name="priority"
            />
          </div>
        </Row>
      </div>
      <div className="mt-3">
        <div>
          <FormikAutocomplete
            getOptionLabel={(opt) => {
              if (opt === null) return '';
              const currentCaseClassification = caseClassifications.find(
                (c) => c.id === opt.id || c.id === opt,
              );
              const currentTranslation = getCurrentTranslation(
                currentCaseClassification?.translations,
                language,
              );
              return currentTranslation ?? '';
            }}
            getValue={(val) => val?.id ?? val}
            id="classificationId"
            isOptionEqualToValue={(option, value) => option.id === value}
            label={t('common:classification')}
            name="classificationId"
            options={caseClassifications}
          />
        </div>
      </div>
    </>
  );
};

export default CaseInfoStep;
